import { useRef, useState } from "react";
import api from "@/lib/api";
import { UploadSimple, Image as ImageIcon, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export default function ImageUpload({ value, onChange, label = "Image", testId = "image-upload" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be < 5 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = () => onChange("");

  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">{label}</label>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testId}-input`} />
      <div className="mt-1 flex items-center gap-3">
        <div className="w-20 h-20 border border-boba-stoneLine rounded-md bg-boba-muted flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={resolveImageUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={28} className="text-stone-400" />
          )}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <button
            type="button"
            onClick={handlePick}
            disabled={uploading}
            className="h-10 px-3 border border-boba-stoneLine rounded-md text-sm font-medium hover:bg-stone-50 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid={`${testId}-button`}
          >
            <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
          </button>
          {value && (
            <button
              type="button"
              onClick={clear}
              className="h-8 px-2 text-xs text-boba-danger hover:underline inline-flex items-center gap-1"
              data-testid={`${testId}-clear`}
            >
              <X size={12} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
